import { API_BASE_URL, STORAGE_KEYS } from '../../utils/constants';
import { getItem } from '../../utils/storage';

const MAX_USER_AGENT_LENGTH = 200;

/**
 * Collect basic, non-sensitive device information for the log entry.
 * @returns {object}
 */
const getDeviceInfo = () => ({
  userAgent: navigator.userAgent.substring(0, MAX_USER_AGENT_LENGTH),
  platform: navigator.platform || 'unknown',
  language: navigator.language,
  screenSize: `${window.screen.width}x${window.screen.height}`,
});

/**
 * Record an access attempt via the backend API (avoids AdBlock blocking Firestore directly).
 * Unauthenticated attempts (userId is null) are not logged.
 *
 * @param {string|null} userId  Firebase UID (null skips logging)
 * @param {'facial'|'pin'|'password'} method  Authentication method used
 * @param {boolean} success  Whether the attempt succeeded
 * @param {string|null} [error]  Error message when success is false
 */
export const logAccess = async (userId, method, success, error = null) => {
  if (!userId) return;
  try {
    const token = await getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) return;
    const res = await fetch(`${API_BASE_URL}/logs/access`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        method,
        success,
        error: error || null,
        device: getDeviceInfo(),
      }),
    });
    if (!res.ok) {
      console.warn('[accessLog] Unexpected response:', res.status);
    }
  } catch (err) {
    // Access logging is best-effort — never block the auth flow
    console.warn('[accessLog] Failed to log access:', err);
  }
};

/**
 * Record a failed access attempt for an unauthenticated user (e.g., failed login
 * before a token is available). Uses the anonymous endpoint with stricter rate limiting.
 *
 * @param {'facial'|'pin'|'password'} method  Authentication method used
 * @param {string|null} [error]  Error message
 */
export const logAnonymousAccessFailure = async (method, error = null) => {
  try {
    await fetch(`${API_BASE_URL}/logs/access/anonymous`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        method,
        device: getDeviceInfo(),
      }),
    });
  } catch (err) {
    console.warn('[accessLog] Failed to log anonymous access:', err);
  }
};

/**
 * Retrieve recent access log entries for a user.
 * NOTE: Reading logs is still done via Firestore directly (admin-only feature,
 * not called from public-facing screens).
 *
 * @param {string} userId  Firebase UID
 * @param {number} [maxEntries=20]
 * @returns {Promise<Array>}
 */
export const getAccessHistory = async (userId, maxEntries = 20) => {
  // This function is only used in admin screens where Firestore access is expected.
  // If needed in the future, create a GET /api/logs/access/:userId backend endpoint.
  return [];
};

/**
 * Count failed access attempts within the last N minutes.
 * NOTE: Kept as stub — returns 0. Move to backend endpoint if needed.
 *
 * @param {string} userId  Firebase UID
 * @param {number} [minutes=60]
 * @returns {Promise<number>}
 */
export const getRecentFailedAttempts = async (userId, minutes = 60) => {
  return 0;
};
